import { cva, cx } from '@vizit/theme';
import type { HTMLAttributes } from 'react';

type SkeletonVariant = 'rounded' | 'square';

interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Whether the skeleton is loaded and the children should be shown.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isLoaded?: boolean;
  /**
   * The display variant of the skeleton.
   *
   * @optional
   * @type {SkeletonVariant}
   * @default 'rectangle'
   */
  variant?: SkeletonVariant;
  /**
   * The element to render: `span` where the skeleton stands in for text inside inline content, e.g. a line in a menu
   * row.
   *
   * @optional
   * @default 'div'
   */
  elementType?: 'div' | 'span';
}

const classes = cva({
  base: '',
  variants: {
    isLoaded: {
      true: 'animate-in',
      false:
        'pointer-events-none flex-shrink-0 animate-skeleton-pulse cursor-default select-none bg-gray-light-100 bg-clip-padding text-transparent before:invisible after:invisible [&>*]:invisible',
    },
    variant: {
      rounded: '',
      square: '',
    },
  },
  compoundVariants: [
    {
      isLoaded: false,
      variant: 'rounded',
      className: 'rounded-full',
    },
    {
      isLoaded: false,
      variant: 'square',
      className: '',
    },
  ],
  defaultVariants: {
    isLoaded: false,
    variant: 'rounded',
  },
});

function Skeleton(props: SkeletonProps) {
  const { className, isLoaded = false, variant = 'rounded', elementType: Element = 'div', ...other } = props;

  return <Element className={cx(classes({ isLoaded, variant }), className)} {...other} />;
}

export { Skeleton };
